import { isNumber, isString } from "lodash";
import React, { useCallback, useMemo } from "react";

import { Order } from "../../../../../../../services";
import { useAppContext } from "../../../../../../../context";
import useTime from "../../../../../../../hooks/useTime";
import { CellContentRoot } from "..";

import cellProcessing from "./cellProcessing";

const TimeToDeliveryCellContent: React.FC<TimeToDeliveryCellContent.Props> = ({
	item,
}) => {
	const { dateFns } = useAppContext();

	const currentTime = useTime("every-second");

	const lastReceivedOrder = useMemo(
		() => item?.additionalFields?.transfer?.lastReceivedOrder,
		[item?.additionalFields?.transfer?.lastReceivedOrder],
	);

	const transferExecutor = useMemo(
		() => lastReceivedOrder?.executorToOrder?.[0],
		[lastReceivedOrder?.executorToOrder],
	);

	const mainExecutor = useMemo(
		() => item.executorToOrder?.[0],
		[item.executorToOrder],
	);

	const mainArrivalTime = useMemo(
		() => mainExecutor?.additionalFields?.arrivalTime,
		[mainExecutor?.additionalFields?.arrivalTime],
	);

	const transferArrivalTime = useMemo(
		() => transferExecutor?.additionalFields?.arrivalTime,
		[transferExecutor?.additionalFields?.arrivalTime],
	);

	const withClients = useMemo(
		() =>
			!!item.executingStages?.with_clients?.timestamp ||
			!!lastReceivedOrder?.executingStages?.with_clients?.timestamp,

		[
			lastReceivedOrder?.executingStages?.with_clients?.timestamp,
			item.executingStages?.with_clients?.timestamp,
		],
	);

	const getTime = useCallback(() => {
		const time = new Date(item.orderDate).getTime();
		if (!Number.isNaN(time)) return item.orderDate;
		return undefined;
	}, [item?.orderDate]);

	const orderDate = useMemo(
		() => (item?.orderDate ? getTime() : item?.orderDate),
		[getTime, item?.orderDate],
	);

	const orderId = useMemo(() => item?.id, [item?.id]);

	const arrivalTime = useMemo(() => {
		let executorArrivalTimestamp: number | undefined | string;

		if (isNumber(mainArrivalTime))
			executorArrivalTimestamp = mainArrivalTime;
		else if (isNumber(transferArrivalTime))
			executorArrivalTimestamp = transferArrivalTime;
		else if (isString(mainArrivalTime))
			executorArrivalTimestamp = Number(mainArrivalTime);
		else if (isString(transferArrivalTime))
			executorArrivalTimestamp = Number(transferArrivalTime);
		else if (orderDate) {
			executorArrivalTimestamp = orderDate;
		}

		const timeLeft =
			executorArrivalTimestamp !== undefined
				? dateFns.getMillisecondsDiff(
						dateFns.toZonedTime(executorArrivalTimestamp).getTime(),
						dateFns.toZonedTime(currentTime).getTime(),
				  )
				: undefined;

		return timeLeft;
	}, [currentTime, dateFns, mainArrivalTime, orderDate, transferArrivalTime]);

	const { content, backgroundColor, textColor } = useMemo(
		() =>
			cellProcessing(
				{
					orderId,
					withClients,
					arrivalTime,
				},
				dateFns,
				currentTime,
			),
		[orderId, withClients, dateFns, currentTime, arrivalTime],
	);

	return (
		<CellContentRoot
			alignItems="center"
			backgroundColor={backgroundColor}
			textColor={textColor}
			h="100%"
			w="100%"
		>
			{content}
		</CellContentRoot>
	);
};

declare namespace TimeToDeliveryCellContent {
	interface Props {
		item: Order.Model;
	}
}

export default TimeToDeliveryCellContent;
